<template>
  <DropdownMenuItem
    class="group flex flex-col gap-1 items-start justify-between py-3 px-4 relative select-none outline-none cursor-pointer data-[disabled]:text-gray-100 data-[disabled]:pointer-events-none data-[highlighted]:text-white data-[highlighted]:bg-[#191B1B] rounded-sm"
    :class="{
      'bg-[#191B1B]': selected,
    }"
    @select="handleSelect"
  >
    <div class="flex justify-between items-center w-full">
      <div class="flex gap-2.5 items-center">
        <Avatar
          :src="iconUrl"
          :alt="name"
          :initials="name?.[0]"
          class="size-8"
        />
        <div>
          <span class="text-sm flex gap-2">
            <div
              class="truncate"
              :class="isGuest ? 'float-left max-w-28' : 'max-w-48'"
              :title="name"
            >
              {{ name }}
            </div>
            <Badge v-if="isGuest" variant="ghost">Guest</Badge>
          </span>
          <span class="block text-sm text-neutral-500">
            <span class="capitalize">{{ subscriptionType }}</span> plan -
            {{ membersCount }} member{{ membersCount > 1 ? 's' : '' }}
          </span>
        </div>
      </div>
      <span>
        <Check v-if="selected" class="text-accent-400" />
      </span>
    </div>
    <div v-if="invitationId !== null" class="flex gap-2.5">
      <span class="size-8">&nbsp;</span>
      <div class="flex gap-2.5">
        <Button
          variant="outline"
          size="sm"
          :disabled="isDeclinePending || isAcceptPending"
          @click="declineInvitation({ id: invitationId })"
        >
          Reject
        </Button>
        <Button
          size="sm"
          :disabled="isDeclinePending || isAcceptPending"
          @click="acceptInvitation({ id: invitationId })"
        >
          Accept invite
        </Button>
      </div>
    </div>
  </DropdownMenuItem>
</template>

<script setup>
import { DropdownMenuItem } from 'radix-vue';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { Avatar } from '@ui/components/avatar';
import requests from '@vuex/requests.js';
import { Button } from '@ui/components/button';
import { Badge } from '@ui/components/badge';
import { queries } from '@/queries/index.js';
import { Check } from 'lucide-vue-next';
import { toast } from 'vue-sonner';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

const props = defineProps({
  iconUrl: {
    type: String,
    default: null,
  },
  name: {
    type: String,
    default: null,
  },
  invitationId: {
    type: Number,
    default: null,
  },
  selected: {
    type: Boolean,
    default: false,
  },
  subscriptionType: {
    type: String,
    default: 'Free',
  },
  membersCount: {
    type: Number,
    default: 1,
  },
  isGuest: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['select']);

function handleSelect(event) {
  if (props.invitationId !== null) {
    event.preventDefault();
  } else {
    emit('select', event);
  }
}

const queryClient = useQueryClient();
const store = useStore();
const router = useRouter();

const { isPending: isAcceptPending, mutate: acceptInvitation } = useMutation({
  mutationFn: async payload => requests.invitations.accept(payload),
  onSuccess: async data => {
    console.log(data);
    store.dispatch('workspace/setWorkspace', { value: data.workspace });
    await queryClient.invalidateQueries({
      queryKey: queries.workspace.connections.queryKey,
    });
    router.push({
      name: 'workspace-projects',
      params: { workspaceHash: data.workspaceHash },
    });
    await queryClient.invalidateQueries();
  },
  onError: err => {
    if (err.response?.data?.exception?.includes('CardException')) {
      toast.error(
        `${err.response.data.message} Please contact the owner of the workspace.`
      );
      return;
    }
    toast.error(err.response?.data?.message || 'An error occurred');
    console.error(err);
  },
});

const { isPending: isDeclinePending, mutate: declineInvitation } = useMutation({
  mutationFn: async payload => {
    await requests.invitations.decline(payload);
  },
  onSuccess: async () => {
    await queryClient.invalidateQueries({
      queryKey: queries.workspace.connections.queryKey,
    });
  },
});
</script>
